import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, QrCodeScanner as QrCodeScannerIcon } from "@mui/icons-material";
import { Html5Qrcode } from "html5-qrcode";
import { apiRequest } from "../config/api";

type GiftVerifyResponse = {
  playerId: string;
  playerName: string;
  giftDescription?: string;
  giftOptions?: string[];
  redeemed: boolean;
  redeemedAt?: any;
  redeemedByAdminName?: string;
};

type GiftToken = {
  tournamentId: string;
  playerId: string;
  sig: string;
};

const SCANNER_ELEMENT_ID = "gift-qr-reader";

const formatDateTime = (value: any): string => {
  if (!value) return "";
  let date: Date | null = null;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (typeof value?.seconds === "number") {
    date = new Date(value.seconds * 1000);
  } else if (typeof value?._seconds === "number") {
    date = new Date(value._seconds * 1000);
  } else {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      date = parsed;
    }
  }

  return date
    ? date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
};

export default function AdminGiftScan() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tournamentId = searchParams.get("t") || "";
  const paramPlayerId = searchParams.get("p") || "";
  const paramSig = searchParams.get("s") || "";

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const handlingScanRef = useRef(false);

  const [scannerActive, setScannerActive] = useState(false);
  const [startingScanner, setStartingScanner] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [error, setError] = useState("");
  const [scanSource, setScanSource] = useState("");
  const [token, setToken] = useState<GiftToken | null>(null);
  const [giftStatus, setGiftStatus] = useState<GiftVerifyResponse | null>(null);
  const [selectedGiftDescription, setSelectedGiftDescription] = useState("");

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;

    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
    } catch {
      // Best effort cleanup.
    }

    try {
      await scanner.clear();
    } catch {
      // Best effort cleanup.
    }

    scannerRef.current = null;
    setScannerActive(false);
  }, []);

  const verifyToken = useCallback(async (nextToken: GiftToken, originLabel: string) => {
    setVerifyLoading(true);
    setError("");
    setScanSource(originLabel);

    try {
      const response = await apiRequest(`/api/tournaments/${nextToken.tournamentId}/gift/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          playerId: nextToken.playerId,
          sig: nextToken.sig,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to verify gift QR code");
      }

      setToken(nextToken);
      setGiftStatus(data);
      setSelectedGiftDescription(data?.giftDescription || "");
    } catch (verifyError: any) {
      setGiftStatus(null);
      setToken(null);
      setSelectedGiftDescription("");
      setError(verifyError?.message || "Failed to verify gift QR code");
    } finally {
      setVerifyLoading(false);
    }
  }, []);

  const parseScannedPayload = useCallback(
    async (decodedText: string) => {
      let parsedUrl: URL;
      try {
        parsedUrl = new URL(decodedText);
      } catch {
        setError("Scanned QR code is not a valid URL payload");
        return;
      }

      const scannedTournamentId = parsedUrl.searchParams.get("t") || "";
      const playerId = parsedUrl.searchParams.get("p") || "";
      const sig = parsedUrl.searchParams.get("s") || "";

      if (!playerId || !sig) {
        setError("Scanned QR code is missing required fields");
        return;
      }

      if (tournamentId && scannedTournamentId && scannedTournamentId !== tournamentId) {
        setError("This QR code belongs to a different tournament");
        return;
      }

      const effectiveTournamentId = tournamentId || scannedTournamentId;
      if (!effectiveTournamentId) {
        setError("Tournament id is missing in both page URL and QR payload");
        return;
      }

      await verifyToken(
        {
          tournamentId: effectiveTournamentId,
          playerId,
          sig,
        },
        "camera",
      );
    },
    [tournamentId, verifyToken],
  );

  const startScanner = useCallback(async () => {
    if (!tournamentId) {
      setError("Missing tournament id (t) in URL");
      return;
    }

    setStartingScanner(true);
    setError("");

    try {
      await stopScanner();
      const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decodedText) => {
          if (handlingScanRef.current) return;
          handlingScanRef.current = true;

          await stopScanner();
          await parseScannedPayload(decodedText);

          handlingScanRef.current = false;
        },
        () => {
          // Ignore scan miss callbacks.
        },
      );

      setScannerActive(true);
    } catch (scannerError: any) {
      setError(scannerError?.message || "Could not start camera scanner");
      await stopScanner();
    } finally {
      setStartingScanner(false);
    }
  }, [parseScannedPayload, stopScanner, tournamentId]);

  useEffect(() => {
    if (!tournamentId) {
      setError("Missing tournament id (t) in URL");
      return;
    }

    startScanner();
    return () => {
      stopScanner();
    };
  }, [startScanner, stopScanner, tournamentId]);

  const handleVerifyFromUrlParams = async () => {
    if (!tournamentId || !paramPlayerId || !paramSig) {
      setError("Current URL does not include p and s params for manual verification");
      return;
    }

    await stopScanner();
    await verifyToken(
      {
        tournamentId,
        playerId: paramPlayerId,
        sig: paramSig,
      },
      "url",
    );
  };

  const handleRedeem = async () => {
    if (!token) return;

    setRedeemLoading(true);
    setError("");

    try {
      const response = await apiRequest(`/api/tournaments/${token.tournamentId}/gift/redeem`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          playerId: token.playerId,
          sig: token.sig,
          giftDescription: selectedGiftDescription || giftStatus?.giftDescription || null,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to redeem gift pickup");
      }

      setGiftStatus(data);
    } catch (redeemError: any) {
      setError(redeemError?.message || "Failed to redeem gift pickup");
    } finally {
      setRedeemLoading(false);
    }
  };

  const handleScanNext = async () => {
    setGiftStatus(null);
    setToken(null);
    setSelectedGiftDescription("");
    setScanSource("");
    setError("");
    await startScanner();
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/admin/tournaments/${tournamentId}`)}>
            Back to Tournament
          </Button>
          <Chip icon={<QrCodeScannerIcon />} label="Gift QR Scanner" color="primary" />
        </Stack>

        <Typography variant="h5" fontWeight={700}>
          Tournament Gift Pickup
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Camera Scanner
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Scan the player QR code to verify and confirm gift pickup.
            </Typography>

            <Box
              id={SCANNER_ELEMENT_ID}
              sx={{
                width: "100%",
                minHeight: 280,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
                p: 1,
                overflow: "hidden",
              }}
            />

            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
              <Button onClick={startScanner} disabled={startingScanner || scannerActive || !tournamentId}>
                {startingScanner ? "Starting..." : "Start Scanner"}
              </Button>
              <Button onClick={stopScanner} disabled={!scannerActive}>
                Pause Scanner
              </Button>
              <Button
                onClick={handleVerifyFromUrlParams}
                disabled={!paramPlayerId || !paramSig || verifyLoading || redeemLoading}
              >
                Verify URL Params
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {(verifyLoading || redeemLoading) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {verifyLoading ? "Verifying gift QR..." : "Confirming pickup..."}
            </Typography>
          </Box>
        )}

        {giftStatus && (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {giftStatus.playerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Gift: {giftStatus.giftDescription || "Gift at organizer discretion"}
                </Typography>
                {!!giftStatus.giftOptions?.length && !giftStatus.redeemed && (
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id="gift-picked-select-label">Picked Gift</InputLabel>
                    <Select
                      labelId="gift-picked-select-label"
                      label="Picked Gift"
                      value={selectedGiftDescription || ""}
                      onChange={(e) => setSelectedGiftDescription(String(e.target.value || ""))}
                    >
                      {giftStatus.giftOptions.map((gift) => (
                        <MenuItem key={gift} value={gift}>
                          {gift}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Typography variant="caption" color="text.secondary">
                  Source: {scanSource === "camera" ? "Camera scan" : "URL params"}
                </Typography>

                {giftStatus.redeemed ? (
                  <Alert severity="success">
                    Already picked up
                    {giftStatus.redeemedByAdminName ? ` by ${giftStatus.redeemedByAdminName}` : ""}
                    {formatDateTime(giftStatus.redeemedAt) ? ` at ${formatDateTime(giftStatus.redeemedAt)}` : ""}.
                  </Alert>
                ) : (
                  <Alert severity="info">Not picked up yet.</Alert>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleRedeem}
                    disabled={giftStatus.redeemed || redeemLoading || verifyLoading}
                  >
                    Confirm Pickup
                  </Button>
                  <Button onClick={handleScanNext} disabled={verifyLoading || redeemLoading}>
                    Scan Next
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Container>
  );
}